// @ts-check

import { createColumnHelper } from "@tanstack/react-table"
import { tableFilterValueIncludes } from "../../../../../components/table/utils/filter.utils"
import { createTaskReportsCommonColumns } from "../../../../../factories/reports/createTaskReportsCommonColumns.factory"
import { buildClientTasksTableCells } from "./ClientTasksTable.cells"
import { applyLeftClickFilter } from "../../../../../utils/tables/filtering.utils"

/**
 *  @typedef {import("../../../../../services/reports.service.types").GlobalTaskReportItem} GlobalTaskReportItem
 *  @typedef {import("@tanstack/react-table").ColumnHelper<GlobalTaskReportItem>} GlobalTaskReportItemColumnHelper
 */

/**
 * @param {import("@tanstack/react-table").Table<GlobalTaskReportItem>} table
 * @param {string} value
 */
function onLeftClickTeamMemberName(table, value) {
  applyLeftClickFilter(table, "teamMemberNameTag", value)
}

/** @param {import("./ClientTasksTable.columns.types").ClientTasksTableColumnsParams} params */
export function buildClientTasksTableColumns(params) {
  const {
    isHoldingModifierDown,
    contextMenuRef,
    setContextMenuTarget,
    userIsAdmin,
    updatingTask,
    hasMultipleProjects,
    teamMemberFilterTemplate,
    categoryFilterTemplate,
    moduleFilterTemplate,
    clientFilterTemplate,
    taskNameFilterTemplate,
  } = params

  const {
    DateCellTemplate,
    ProjectCodeCellTemplate,
    ModuleCellTemplate,
    TeamMemberCellTemplate,
    CategoryCellTemplate,
    TaskCellTemplate,
    SisuTimeCellTemplate,
    SisuTimeFooterTemplate,
    BilledTimeCellTemplate,
    BilledTimeFooterTemplate,
    TimeDifferenceCellTemplate,
    TimeDifferenceFooterTemplate,
    ActionsCellTemplate,
  } = buildClientTasksTableCells({
    isHoldingModifierDown,
    onLeftClickTeamMemberName,
    contextMenuRef,
    setContextMenuTarget,
    userIsAdmin,
    updatingTask,
  })

  /** @type {import("../../../../../factories/reports/createTaskReportsCommonColumns.factory.types").TaskReportCommonColumnConfig<GlobalTaskReportItem>} */
  const config = {
    date: {
      cellTemplate: DateCellTemplate,
      footerTemplate: hasMultipleProjects ? "" : "Totales",
    },
    projectCode: {
      cellTemplate: ProjectCodeCellTemplate,
      filterElement: clientFilterTemplate,
      filterFn: (row, _columnId, filterValue) =>
        tableFilterValueIncludes(row.original.projectName, filterValue),
      footerTemplate: hasMultipleProjects ? "Totales" : "",
      size: 102,
    },
    module: {
      cellTemplate: ModuleCellTemplate,
      filterElement: moduleFilterTemplate,
      filterFn: (row, _columnId, filterValue) =>
        tableFilterValueIncludes(row.original.moduleId, filterValue),
    },
    categoryTitle: {
      cellTemplate: CategoryCellTemplate,
      filterElement: categoryFilterTemplate,
      filterFn: (row, _columnId, filterValue) => {
        const { categoryId, specialCategoryId } = row.original
        return tableFilterValueIncludes(
          [`normal:${categoryId}`, `special:${specialCategoryId}`],
          filterValue
        )
      },
    },
    task: {
      cellTemplate: TaskCellTemplate,
      filterElement: taskNameFilterTemplate,
    },
    sisuTime: {
      cellTemplate: SisuTimeCellTemplate,
      footerTemplate: SisuTimeFooterTemplate,
    },
    billedTime: {
      cellTemplate: BilledTimeCellTemplate,
      footerTemplate: BilledTimeFooterTemplate,
    },
    timeDifference: {
      cellTemplate: TimeDifferenceCellTemplate,
      footerTemplate: TimeDifferenceFooterTemplate,
    },
    actions: {
      cellTemplate: ActionsCellTemplate,
    },
  }

  /** @type {GlobalTaskReportItemColumnHelper} */
  const columnHelper = createColumnHelper()

  const {
    date,
    projectCode,
    module,
    categoryTitle,
    task,
    sisuTime,
    billedTime,
    timeDifference,
    actions,
  } = createTaskReportsCommonColumns({
    columnHelper,
    config,
  })

  return [
    projectCode,
    date,

    columnHelper.accessor("teamMemberNameTag", {
      header: "Integrante",
      cell: TeamMemberCellTemplate,
      enableSorting: false,
      filterFn: "arrIncludesSome",
      meta: {
        filterElement: teamMemberFilterTemplate,
        className: "team-member-column",
      },
    }),

    module,
    categoryTitle,
    task,
    sisuTime,
    billedTime,
    timeDifference,
    actions,
  ]
}
